// Login Page - Modern Design with Security Features
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [lockTime, setLockTime] = useState(0);
  const emailRef = useRef<HTMLInputElement>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Anti-brute-force: Track failed attempts
  useEffect(() => {
    if (attempts >= 5) {
      setLocked(true);
      const timer = 60;
      setLockTime(timer);
      const interval = setInterval(() => {
        setLockTime(prev => {
          if (prev <= 1) {
            setLocked(false);
            setAttempts(0);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [attempts]);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    if (!re.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (password: string) => {
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    if (!isEmailValid || !isPasswordValid) return;
    if (locked) {
      toast.error(`Too many failed attempts. Please wait ${lockTime} seconds.`);
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error: any) {
      setAttempts(prev => prev + 1);
      toast.error(error?.response?.data?.error || 'Login failed. Please check your credentials.');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-base-100 via-pink-950/20 to-amber-950/20 p-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-pink-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      </div>
      <div className="card bg-base-100/80 backdrop-blur-xl shadow-2xl w-full max-w-md relative z-10 border border-white/10">
        <div className="card-body p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center gap-3 mb-6 group">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-amber-400 rounded-2xl flex items-center justify-center shadow-glow-gold group-hover:scale-105 transition-transform duration-300">
                <span className="text-white font-black text-2xl">EG</span>
              </div>
            </Link>
            <h1 className="font-heading text-3xl font-bold bg-gradient-to-r from-white via-pink-200 to-amber-200 bg-clip-text text-transparent">
              Welcome Back
            </h1>
            <p className="text-gray-400 mt-2">Sign in to your Elite Gymnastics account</p>
          </div>
          {locked && (
            <div className="alert alert-error mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Too many failed attempts. Try again in {lockTime}s</span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="form-control">
              <label className="label"><span className="label-text text-gray-300 font-medium">Email Address</span></label>
              <div className="relative">
                <input
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                  onBlur={() => validateEmail(email)}
                  className={`input input-bordered w-full pl-12 bg-base-200/50 border-white/10 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 ${emailError ? 'input-error' : ''}`}
                  placeholder="your@email.com"
                  required
                  disabled={loading || locked}
                  autoComplete="email"
                />
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              {emailError && <label className="label"><span className="label-text-alt text-error">{emailError}</span></label>}
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text text-gray-300 font-medium">Password</span></label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setPasswordError(''); }}
                  onBlur={() => validatePassword(password)}
                  className={`input input-bordered w-full pl-12 pr-12 bg-base-200/50 border-white/10 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 ${passwordError ? 'input-error' : ''}`}
                  placeholder="Enter password"
                  required
                  disabled={loading || locked}
                  autoComplete="current-password"
                />
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {passwordError && <label className="label"><span className="label-text-alt text-error">{passwordError}</span></label>}
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="checkbox checkbox-sm checkbox-primary" />
                <span className="text-gray-400">Remember me</span>
              </label>
              <Link to="/auth/forgot-password" className="text-pink-400 hover:text-pink-300 transition-colors">
                Forgot password?
              </Link>
            </div>
            <button
              type="submit"
              className={`btn w-full h-12 bg-gradient-to-r from-pink-500 to-amber-400 border-none text-white font-semibold hover:shadow-glow-gold hover:scale-[1.02] transition-all ${loading ? 'loading' : ''}`}
              disabled={loading || locked}
            >
              {loading ? <span className="loading loading-spinner loading-sm" /> : locked ? `Locked (${lockTime}s)` : 'Sign In'}
            </button>
            {attempts > 0 && attempts < 5 && (
              <p className="text-center text-sm text-amber-400">{5 - attempts} attempts remaining</p>
            )}
          </form>
          <div className="divider text-gray-500 text-sm my-6">Don't have an account?</div>
          <Link to="/register" className="btn btn-outline w-full border-pink-500/50 text-pink-400 hover:bg-pink-500/10 hover:border-pink-400">
            Create Account
          </Link>
        </div>
      </div>
    </div>
  );
}